import { useLayoutEffect, useRef } from 'react'

import { useNavigationType, useParams } from 'react-router-dom'
import usePostStatsContext from '@contexts/postStatsContext'
import useVirtuosoSnapshot from '@hooks/useVirtuosoSnapshot'
import { localAccessors } from '@utils/localStateAccess'
import { size } from '@styles/theme'
import useFeed from '@hooks/useFeed'

import { CircularProgress } from '@mui/material'
import { EmptyFeed } from '@elements/EmptyState'
import Post from '@components/feeds/post/Post'
import { Virtuoso } from 'react-virtuoso'
import EndDot from '@elements/EndDot'

const SocialProfileRepostsFeed = ({ followUser, unfollowUser, profile = {}, isOwnProfile, selectedTab, setSelectedTab, customScrollParent }) => {
  const params = useParams()
  const postStats = usePostStatsContext()
  const navigationType = useNavigationType()
  const listRef = useRef()
  const isOnSeletedTab = selectedTab === 1

  // const useFeedUrls = {
    // 0: { getPostsUrl: `/api/users/${params?.id}/posts` },
    // 1: { getPostsUrl: `/api/users/${params?.id}/replies` },
    // 2: { getPostsUrl: `/api/users/${params?.id}/posts_with_media` },
    // 3: { getPostsUrl: `/api/users/${params?.id}/liked_posts` },
  // }

  const useFeedParams = {
    // postStateCallback: (storedVal, defaultVal) => navigationType === 'POP' ? storedVal : defaultVal,
    feedAccessor: localAccessors.socialProfileRepostPosts(params.id),
    getPostsUrl: `/api/users/${params?.id}/reposts`,
    postStats,
  }

  const feedState = useFeed({ ...useFeedParams })
  const {
    posts,
    setPostsLoaded,
    postsLoaded,
    getInitialPosts,
    postsEmpty,

    noMorePosts,
    setNoMorePosts,
    getMorePosts,

    setLikeToState,
    setUnlikeToState,
    setRepostToState,
    newReplyAfterCreate,
    afterPostDelete,

    feedAccessor,
    firstRender,
  } = feedState

  const {
    restoreStateProp,
  } = useVirtuosoSnapshot(
    `social-profile_virtuoso-snapshot_${params?.id}`,
    firstRender,
    listRef,
  )

  // Gets user posts when selected tab changes, and on initial render
  useLayoutEffect(() => {
    if (!shouldGetInitialPosts()) return;
    if (handlePostsFromBacknavigation()) return;
    // Load post data as normal if not backnavigating, and no existing post data.
    getInitialPosts()
  }, [selectedTab, params.id])

  const shouldGetInitialPosts = () => {
    if (isOnSeletedTab && firstRender.current === true) return true
    else return false
  }

  const handlePostsFromBacknavigation = () => {
    // If the user backnavigates, and the component has just rendered with posts, then don't load new posts
    if (didBacknavigate()) {
      firstRender.current = false
      setPostsLoaded(true)
      if (posts.length < 25) setNoMorePosts(true)
      return true
    }
    return false
  }

  const didBacknavigate = () => (
    firstRender.current === true && navigationType === 'POP' && posts.length > 0
  )

  return (
    <Virtuoso
      customScrollParent={customScrollParent}
      // we're doing this weird thing with the data to account for the SocialProfileMain component needing to be rendered within the itemcontent of the list. Cause shit sucks.
      data={posts}
      minOverscanItemCount={7}
      endReached={!noMorePosts && isOnSeletedTab && !didBacknavigate() && getMorePosts}
      atBottomThreshold={window.innerHeight*3}
      ref={node => { if (node) listRef.current = node  }}
      context={{
        profile,
        followUser,
        unfollowUser,
        isOwnProfile,
        selectedTab,
        setSelectedTab,
      }}
      { ...(navigationType === 'POP' ? restoreStateProp : []) }
      itemContent={(index, post) => (
        <Post
          key={index}
          post={post}
          index={index - 1}
          beforeLikeHook={setLikeToState}
          beforeUnlikeHook={setUnlikeToState}
          beforeRepostHook={setRepostToState}
          newReplyAfterCreate={newReplyAfterCreate}
          afterDelete={afterPostDelete}
          feedAccessor={feedAccessor}
        />
      )}
      components={{
        Footer: () => {
          const renderFooterContent = () => {
            if (!postsLoaded) return (
              <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress style={{ margin: '1rem', color: '#6A23FF' }} thickness={8} />
              </div>
            ); else if (postsLoaded && posts?.length > 0) return (
              <EndDot />
            ); else if (postsEmpty) return (
              <EmptyFeed
                containerProps={{ style: { padding: size(5) }}}
                textProps={{ style: { textAlign: 'center' } }}
                text={<span>{profile?.displayName} has not reposted anything yet.</span>}
                noIcon
              />
            )
          }
          return (
            <div style={{ paddingBottom: window.screen.height*0.75 || size(30) }}>
              {renderFooterContent()}
            </div>
          )
        }
      }}
    />
  )
}

export { SocialProfileRepostsFeed }
